import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Button from '@mui/material/Button'
import Collapse from '@mui/material/Collapse'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { TripDetailsContext } from 'context/trips/TripDetailsContext'
import { type ReactNode, useCallback, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import type { TripData } from 'screens/TripDashboard/TripDashboard.types'
import StopGroupActions from 'screens/TripDetails/StopsView/StopGroupActions'
import StopGroupHeader from 'screens/TripDetails/StopsView/StopGroupHeader'
import type { StopsSequenceGroup } from 'screens/TripDetails/TripDetails.types'

interface StopGroupListProps {
  groups: StopsSequenceGroup[]
  trip: TripData
  weblinkToken?: string
}

const StopGroupList = ({
  groups,
  trip,
  weblinkToken,
}: StopGroupListProps): ReactNode => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [tripDetailsState, tripDetailsDispatch] = useContext(TripDetailsContext)

  const handleOnChange = useCallback((groupId: number | null) => () => {
    tripDetailsDispatch({
      type: 'setActiveStopsGroup',
      payload: groupId,
    })
  }, [])

  return (
    <>
      <Collapse in={isMobile && tripDetailsState.activeStopsGroup !== null}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArrowBackIcon />}
          sx={{ marginBottom: 1 }}
          onClick={handleOnChange(null)}
        >
          {t('screens.cs.trip_details.all_stops')}
        </Button>
      </Collapse>
      <Paper
        sx={{
          overflowY: 'auto',
        }}
      >
        <Stack direction="column">
          {groups.filter((group) => isMobile ? (tripDetailsState.activeStopsGroup === null || tripDetailsState.activeStopsGroup === group.id) : true).map((group) => (
            <Accordion
              key={group.id}
              expanded={tripDetailsState.activeStopsGroup === group.id}
              onChange={handleOnChange(group.id)}
              sx={{
                backgroundColor: (!isMobile && tripDetailsState.activeStopsGroup === group.id) ? theme.palette.lightblue[5] : undefined,
                '&:hover': {
                  backgroundColor: !isMobile ? theme.palette.lightblue[5] : undefined,
                },
              }}
            >
              <AccordionSummary expandIcon={!isMobile && <ExpandMoreIcon />}>
                <StopGroupHeader group={group} />
              </AccordionSummary>
              <AccordionDetails>
                <StopGroupActions
                  group={group}
                  trip={trip}
                  weblinkToken={weblinkToken}
                />
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </Paper>
    </>
  )
}

export default StopGroupList
